/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { createOperationCall, OperationHandler } from 'src/core/services/api/createOperationCall';
import { fileService } from 'src/core/services/api/fileService';
import { httpService } from 'src/core/services/api/httpService';
import { AppSettings, AzureAccount } from 'src/core/stores/app/types';
import { ServiceKey } from 'src/core/stores/AzureSessionStore';
import { CultureStore } from 'src/core/stores/CultureStore';
import { FileType } from 'src/core/types/FileType';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { AppAzureCsAccountSwagger, AppCultureSwagger, AppSettingsSwagger } from 'src/luis/core/services/apps/appSwaggers';
import { LuisAppStore } from 'src/luis/core/stores/LuisAppStore';
import {
    appAzureCsAccountSwaggerToAppAzureCsAccountStore,
    appCultureSwaggerToAppCultureStore
} from 'src/luis/modules/apps/handlers/luisAppMappers';
import { AppSwagger } from 'src/luis/projects/apps/types/swagger/AppSwagger';
import { LuisId } from 'src/luis/types/Resource';

export const getLuisAppService = createService(({ context, getEnvironment }) => {
    const { webApiUrl, isProduction } = getEnvironment();
    const { webApiUrl: webApiUrlV3, apiUrl: apiUrlV3 } = getEnvironment({ apiVersion: 'v3.0-preview' });
    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .build();

    const getBaseUrl = (id?: LuisId) =>
        urlBuilder()
            .baseUrl(apiUrlV3)
            .app(id ?? context.appId);

    const getApps = async (serviceKey?: ServiceKey): Promise<AppSwagger[]> => {
        const customApiOptions = luisRequestOptionsBuilder()
            .useDefaultOptions(serviceKey?.value ? serviceKey : context.serviceKey, isProduction)
            .build();

        const url = urlBuilder()
            .baseUrl(apiUrlV3)
            .app()
            .queryParam('take', `${LuisConstants.ResourceLimits.ServiceFetchSizeLimit}`)
            .build();

        const { data: appItems } = await httpService.get<AppSwagger[]>(url, customApiOptions);

        return appItems;
    };

    const getAppById = async ({ id }: Pick<LuisAppStore, 'id'>): Promise<AppSwagger> => {
        const { data: newAppItem } = await httpService.get<AppSwagger>(getBaseUrl(id).build(), apiOptions);

        return newAppItem;
    };

    /**
     * Loads the applications that have composite entities
     * which means that they need an entity migration.
     */
    const getAppsToUpgrade = async (): Promise<AppSwagger[]> => {
        const url = urlBuilder()
            .baseUrl(webApiUrl)
            .resource('compositesMigration')
            .resource('needMigrationApps')
            .build();

        const { data: apps } = await httpService.get<{ apps: AppSwagger[] }>(url, apiOptions);
        const { apps: appsToUpgrade } = apps;

        return appsToUpgrade;
    };

    const getAppSettings = async (): Promise<AppSettingsSwagger> => {
        const url = getBaseUrl().resource('settings');

        const { data: appSettings } = await httpService.get<AppSettingsSwagger>(url.build(), apiOptions);

        return appSettings;
    };

    const getAppCultures = async (): Promise<CultureStore[]> => {
        const url = urlBuilder()
            .baseUrl(apiUrlV3)
            .app()
            .resource('cultures')
            .build();

        const { data: cultures } = await httpService.get<AppCultureSwagger[]>(url, apiOptions);

        cultures.forEach(c => {
            LuisConstants.Luis.Cultures.PreviewCultures.includes(c.code) ? (c.name = `${c.name} - (Preview)`) : c.name;
        });

        return (<AppCultureSwagger[]>cultures).map(appCultureSwaggerToAppCultureStore);
    };

    const createApp = async ({
        name,
        description,
        culture
    }: Pick<LuisAppStore, 'name' | 'description' | 'culture'>): Promise<AppSwagger> => {
        const url = urlBuilder()
            .baseUrl(apiUrlV3)
            .app()
            .build();

        const { data: newAppId } = await httpService.post<string>(url, { name, description, culture }, apiOptions);

        return await getAppById({ id: newAppId });
    };

    const updateApp = async ({ name, description }: Pick<LuisAppStore, 'name' | 'description'>): Promise<void> => {
        await httpService.put(getBaseUrl().build(), { name, description }, apiOptions);
    };

    const updateAppSettings = async ({ isPublic }: Omit<AppSettings, 'id'>): Promise<void> => {
        const url = getBaseUrl()
            .resource('settings')
            .build();

        await httpService.put<AppSettingsSwagger>(url, { public: isPublic }, apiOptions);
    };

    const importApp = async ({
        appFileString,
        name,
        fileType
    }: {
        appFileString: string;
        name: string;
        fileType: FileType;
    }): Promise<AppSwagger> => {
        const url = urlBuilder()
            .baseUrl(apiUrlV3)
            .app()
            .resource('import')
            .queryParam('appName', name)
            .build();

        const options = luisRequestOptionsBuilder()
            .useProperServiceKey(context.serviceKey, isProduction)
            .useFileContent(fileType)
            .build();

        const { data: importedAppId } = await fileService.upload<string>(url, appFileString, options);

        return getAppById({ id: importedAppId });
    };

    const exportApp = async ({ fileName, fileType }: { fileName: string; fileType: FileType }): Promise<void> => {
        const url = urlBuilder()
            .baseUrl(apiUrlV3)
            .app(context.appId)
            .version(context.versionId)
            .resource('export')
            .queryParam('format', fileType)
            .build();

        const options = luisRequestOptionsBuilder()
            .useProperServiceKey(context.serviceKey, isProduction)
            .useFileContent(fileType)
            .build();

        await fileService.download(url, `${fileName}.${fileType}`, options);
    };

    const importContainerEndpointLogs = async ({ logsCsvFile }: { logsCsvFile: FormData }): Promise<void> => {
        const url = urlBuilder()
            .baseUrl(webApiUrlV3)
            .app(context.appId)
            .resource('unlabeled')
            .build();

        const options = luisRequestOptionsBuilder()
            .useProperServiceKey(context.serviceKey, isProduction)
            .useFileContent('csv')
            .build();

        await fileService.upload(url, logsCsvFile, options);
    };

    const initExportingEndpointLogs = async (): Promise<void> => {
        const url = getBaseUrl()
            .resource('queryLogsAsync')
            .build();

        await httpService.post(url, undefined, apiOptions);
    };

    const exportAppForContainer = async ({ slot }: { slot: string }): Promise<void> => {
        const url = urlBuilder()
            .baseUrl(webApiUrl)
            .resource('package', context.appId)
            .resource('slot', slot)
            .resource('gzip')
            .build();

        await fileService.download(url, `${context.appId}_${slot}.gz`, apiOptions);
    };

    const checkExportingEndpointLogs = async (handler: OperationHandler<void>): Promise<void> => {
        const url = getBaseUrl()
            .resource('queryLogsAsync')
            .build();

        await createOperationCall<void>(url, apiOptions, handler);
    };

    const exportEndpointLogs = async (): Promise<void> => {
        const url = getBaseUrl()
            .resource('queryLogsAsync')
            .build();

        const options = luisRequestOptionsBuilder()
            .useProperServiceKey(context.serviceKey, isProduction)
            .useFileContent('csv')
            .build();

        await fileService.download(url, `${context.appId}_logs.csv`, options);
    };

    const deleteApp = async (): Promise<void> => {
        await httpService.delete(getBaseUrl().build(), apiOptions);
    };

    /**
     * Gets the Azure Cs accounts assigned to the app
     * with the given id.
     */
    const getAppAzureCsAccounts = async (): Promise<AzureAccount[]> => {
        const baseUrl = () => urlBuilder().baseUrl(webApiUrl);
        const url = baseUrl()
            .app(context.appId)
            .resource('subscriptions')
            .build();

        const { data: accounts } = await httpService.get<AppAzureCsAccountSwagger[]>(url, apiOptions);

        return accounts.map(appAzureCsAccountSwaggerToAppAzureCsAccountStore);
    };

    /**
     * Adds the given Azure account to the prediction endpoints
     * of the application with the given id.
     *
     * @param param0.id The id of the resource to add.
     * @param param0.path The path of the Azure resource.
     * @param param0.region The region of the Azure resource.
     */
    const assignAppAzureCsAccount = async ({ id, path, region }: Partial<AzureAccount>): Promise<void> => {
        const builder = urlBuilder()
            .baseUrl(webApiUrl)
            .app(context.appId)
            .resource('subscriptions')
            .build();

        await httpService.post(builder, { subscriptionId: id, keyPath: path, region }, apiOptions);
    };

    /**
     * Un-assigns the given Azure account from the application
     * with the given application id.
     *
     * @param param0.id The id of the resource to un-assign.
     */
    const unAssignAppAzureCsAccount = async ({ id }: Pick<AzureAccount, 'id'>): Promise<void> => {
        const builder = urlBuilder()
            .baseUrl(webApiUrl)
            .app(context.appId)
            .resource('subscriptions', id)
            .build();

        await httpService.delete(builder, apiOptions);
    };

    /**
     * Changes the authoring resource for the given
     * app id in the context.
     */
    const changeAppAuthoringCsAccount = async ({
        azureSubscriptionId,
        resourceGroup,
        accountName
    }: {
        azureSubscriptionId: string;
        resourceGroup: string;
        accountName: string;
    }) => {
        const url = urlBuilder()
            .baseUrl(apiUrlV3)
            .app(context.appId)
            .resource('move')
            .build();

        await httpService.post(url, { azureSubscriptionId, resourceGroup, accountName }, apiOptions);
    };

    return {
        getApps,
        updateApp,
        deleteApp,
        createApp,
        importApp,
        exportApp,
        getAppById,
        getAppCultures,
        getAppSettings,
        getAppsToUpgrade,
        updateAppSettings,
        exportEndpointLogs,
        exportAppForContainer,
        getAppAzureCsAccounts,
        assignAppAzureCsAccount,
        initExportingEndpointLogs,
        unAssignAppAzureCsAccount,
        checkExportingEndpointLogs,
        importContainerEndpointLogs,
        changeAppAuthoringCsAccount
    };
});
